import React, { useEffect, useRef, useState } from 'react'
import { ImageIcon, Loader2Icon, SendIcon, XIcon } from 'lucide-react'
import {
  CHAT_ATTACHMENT_ACCEPT,
  chatDirectoryDisplayName,
  fetchChatDirectoryUsers,
  type ChatDirectoryUser,
} from '../../api/teamChat'

interface ChatComposeBarProps {
  value: string
  onChange: (value: string) => void
  onSend: (file?: File | null, mentionedUserIds?: string[]) => Promise<void> | void
  disabled?: boolean
  sending?: boolean
  placeholder?: string
  /** Compact layout for embedded job panels */
  compact?: boolean
  /** Enable @mention picker (Team discuss + Internal chat). Default true. */
  enableMentions?: boolean
}

type MentionDraft = { id: string; name: string }

function findActiveMention(
  text: string,
  cursor: number
): { start: number; query: string } | null {
  const before = text.slice(0, cursor)
  const match = before.match(/(^|[\s([{\n])@([^\s@]*)$/)
  if (!match) return null
  const atIndex = before.lastIndexOf('@')
  if (atIndex < 0) return null
  return { start: atIndex, query: match[2] || '' }
}

export function ChatComposeBar({
  value,
  onChange,
  onSend,
  disabled,
  sending,
  placeholder = 'Type a message…',
  compact,
  enableMentions = true,
}: ChatComposeBarProps) {
  const fileRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [directory, setDirectory] = useState<ChatDirectoryUser[]>([])
  const [directoryLoaded, setDirectoryLoaded] = useState(false)
  const [mentionOpen, setMentionOpen] = useState(false)
  const [mentionStart, setMentionStart] = useState<number | null>(null)
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionIndex, setMentionIndex] = useState(0)
  const [trackedMentions, setTrackedMentions] = useState<MentionDraft[]>([])

  const canSend = Boolean(value.trim() || pendingFile) && !disabled && !sending

  useEffect(() => {
    if (!enableMentions || directoryLoaded || disabled) return
    let cancelled = false
    fetchChatDirectoryUsers()
      .then((rows) => {
        if (!cancelled) {
          setDirectory(rows)
          setDirectoryLoaded(true)
        }
      })
      .catch(() => {
        if (!cancelled) setDirectoryLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [enableMentions, directoryLoaded, disabled])

  const filteredMentions = enableMentions
    ? directory
        .filter((u) => {
          const q = mentionQuery.trim().toLowerCase()
          if (!q) return true
          const label = chatDirectoryDisplayName(u).toLowerCase()
          const email = (u.email || '').toLowerCase()
          return label.includes(q) || email.includes(q)
        })
        .slice(0, 8)
    : []

  const clearPending = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPendingFile(null)
    setPreviewUrl(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPendingFile(file)
    setPreviewUrl(file.type.startsWith('image/') ? URL.createObjectURL(file) : null)
  }

  const closeMention = () => {
    setMentionOpen(false)
    setMentionStart(null)
    setMentionQuery('')
    setMentionIndex(0)
  }

  const syncMentionFromValue = (next: string, cursor: number) => {
    if (!enableMentions) {
      closeMention()
      return
    }
    const active = findActiveMention(next, cursor)
    if (!active) {
      closeMention()
      return
    }
    setMentionOpen(true)
    setMentionStart(active.start)
    setMentionQuery(active.query)
    setMentionIndex(0)
  }

  const insertMention = (user: ChatDirectoryUser) => {
    if (mentionStart == null) return
    const name = chatDirectoryDisplayName(user)
    const cursor = textareaRef.current?.selectionStart ?? value.length
    const before = value.slice(0, mentionStart)
    const after = value.slice(cursor)
    const insertion = `@${name} `
    const next = before + insertion + after
    onChange(next)
    setTrackedMentions((prev) => {
      if (prev.some((m) => m.id === user.id)) return prev
      return [...prev, { id: user.id, name }]
    })
    closeMention()
    requestAnimationFrame(() => {
      const el = textareaRef.current
      if (!el) return
      const pos = before.length + insertion.length
      el.focus()
      el.setSelectionRange(pos, pos)
    })
  }

  const resolveMentionIds = (text: string): string[] => {
    return trackedMentions
      .filter((m) => text.includes(`@${m.name}`))
      .map((m) => m.id)
  }

  const handleSend = async () => {
    if (!canSend) return
    const file = pendingFile
    const mentionIds = resolveMentionIds(value)
    await onSend(file, mentionIds)
    clearPending()
    setTrackedMentions([])
    closeMention()
  }

  return (
    <div className="space-y-2">
      {pendingFile && (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2">
          {previewUrl ? (
            <img src={previewUrl} alt="" className="h-14 w-14 rounded object-cover" />
          ) : (
            <div className="h-14 w-14 rounded bg-gray-200 flex items-center justify-center text-xs text-gray-600">
              File
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm text-gray-900 truncate">{pendingFile.name}</p>
            <p className="text-xs text-gray-500">{Math.round(pendingFile.size / 1024)} KB</p>
          </div>
          <button
            type="button"
            onClick={clearPending}
            className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500"
            aria-label="Remove attachment"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className="relative flex gap-2 items-end">
        <input
          ref={fileRef}
          type="file"
          accept={CHAT_ATTACHMENT_ACCEPT}
          className="hidden"
          onChange={onPickFile}
          disabled={disabled || sending}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={disabled || sending}
          className="min-h-[44px] min-w-[44px] px-2 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 self-end flex items-center justify-center"
          aria-label="Attach image"
          title="Attach image"
        >
          <ImageIcon className="w-4 h-4" />
        </button>
        <div className="relative flex-1 min-w-0">
          {mentionOpen && filteredMentions.length > 0 && (
            <div className="absolute bottom-full left-0 right-0 mb-1 z-20 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
              {filteredMentions.map((u, i) => {
                const label = chatDirectoryDisplayName(u)
                return (
                  <button
                    key={u.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      insertMention(u)
                    }}
                    className={`w-full text-left px-3 py-2 text-sm ${
                      i === mentionIndex ? 'bg-blue-50 text-blue-900' : 'hover:bg-gray-50 text-gray-800'
                    }`}
                  >
                    <div className="font-medium truncate">@{label}</div>
                    {(u.role || u.email) && (
                      <div className="text-xs text-gray-500 truncate">
                        {[u.role, u.email].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => {
              const next = e.target.value
              onChange(next)
              syncMentionFromValue(next, e.target.selectionStart ?? next.length)
            }}
            onSelect={(e) => {
              const el = e.currentTarget
              syncMentionFromValue(el.value, el.selectionStart ?? el.value.length)
            }}
            onBlur={() => {
              // Delay so mousedown on picker can fire first
              window.setTimeout(() => closeMention(), 150)
            }}
            rows={compact ? 2 : 2}
            placeholder={
              enableMentions && placeholder === 'Type a message…'
                ? 'Type a message… Use @ to ping someone'
                : placeholder
            }
            disabled={disabled || sending}
            className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            onKeyDown={(e) => {
              if (mentionOpen && filteredMentions.length > 0) {
                if (e.key === 'ArrowDown') {
                  e.preventDefault()
                  setMentionIndex((i) => (i + 1) % filteredMentions.length)
                  return
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault()
                  setMentionIndex((i) => (i - 1 + filteredMentions.length) % filteredMentions.length)
                  return
                }
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  insertMention(filteredMentions[mentionIndex])
                  return
                }
                if (e.key === 'Escape') {
                  e.preventDefault()
                  closeMention()
                  return
                }
                if (e.key === 'Tab') {
                  e.preventDefault()
                  insertMention(filteredMentions[mentionIndex])
                  return
                }
              }
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void handleSend()
              }
            }}
          />
        </div>
        <button
          type="button"
          onClick={() => void handleSend()}
          disabled={!canSend}
          className="min-h-[44px] min-w-[44px] px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 self-end flex items-center justify-center gap-2"
          aria-label="Send"
        >
          {sending ? <Loader2Icon className="w-4 h-4 animate-spin" /> : <SendIcon className="w-4 h-4" />}
          {!compact && <span className="hidden sm:inline">Send</span>}
        </button>
      </div>
    </div>
  )
}
